import { NavItem } from "./navItems";

export type AccessUser = {
  isOwner?: boolean;
  isOperator?: boolean;
  isAdmin?: boolean;
  isSuperuser?: boolean;
  isStaff?: boolean;
  isContact?: boolean;
  canViewCalendar?: boolean;
  locations?: string[];
  accountMode?: "lead_only" | "full" | null;
};

const LEAD_ONLY_ALLOWED_PATHS = [
  "/inbox",
  "/calendar",
  "/growth",
  "/marketplace",
  "/account-settings",
  "/profile",
  "/organization",
  "/subscription",
  "/stripe",
];

export const isLeadOnlyAllowedPath = (pathname: string): boolean =>
  LEAD_ONLY_ALLOWED_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );

export const isContactRole = (user: AccessUser): boolean =>
  !!user.isContact;

export const isContactUser = (user: AccessUser): boolean =>
  isContactRole(user) && !hasOrgAccess(user);

export const isOperatorUser = (user: AccessUser): boolean =>
  !!(user.isOperator && !user.isOwner && !user.isAdmin && !user.isSuperuser);

export const hasCalendarAccess = (user: AccessUser): boolean =>
  !!(user.isOwner || user.isAdmin || user.isSuperuser || user.canViewCalendar);

export const hasMarketplaceAccess = (user: AccessUser): boolean =>
  !!(user.isOwner || user.isAdmin || user.isSuperuser);

export const hasOrgAccess = (user: AccessUser): boolean =>
  !!(user.isOwner || user.isAdmin || user.isOperator || user.isSuperuser);

export const hasOrganizationFinancialAccess = (
  user: AccessUser & { viewMode?: string },
): boolean =>
  user.viewMode !== "contact" && !!(user.isOwner || user.isAdmin || user.isSuperuser);

export const isContactOnlyUser = (user: AccessUser): boolean =>
  isContactRole(user) && !hasOrgAccess(user);

/** Keep account deep links canonical and in the active view, preserving return parameters. */
export const accountRouteRedirect = (
  user: AccessUser & { viewMode?: string },
  location: { pathname: string; search: string; hash: string },
): string | null => {
  if (location.pathname.replace(/\/$/, "") === "/profile") return "/organization" + location.search + location.hash;
  if (!["/account-settings", "/contact/account-settings"].includes(location.pathname)) return null;
  const contact = isContactOnlyUser(user) || (user.viewMode === "contact" && isContactRole(user));
  const target = contact ? "/contact/account-settings" : "/account-settings";
  return location.pathname === target ? null : target + location.search + location.hash;
};

export const isOrglessUser = (user: AccessUser): boolean =>
  !isContactUser(user) && !hasOrgAccess(user);

const OPERATOR_ALLOWED_PATHS = ["/calendar", "/routes", "/growth", "/tasks", "/account-settings", "/help-center/routes"];

export const operatorHomePath = (user: AccessUser): string =>
  user.accountMode === "lead_only" ? "/growth/leads" : "/routes";

export const isOperatorAllowedPath = (pathname: string, user?: AccessUser): boolean =>
  !(pathname === "/growth/marketplace" || pathname.startsWith("/growth/marketplace/")) &&
  !(user?.accountMode === "lead_only" && (pathname === "/routes" || pathname.startsWith("/routes/"))) &&
  (!(pathname === "/calendar" || pathname.startsWith("/calendar/")) || !user || hasCalendarAccess(user)) && OPERATOR_ALLOWED_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );

export const filterNavItemsForUser = (
  items: NavItem[],
  user: AccessUser,
): NavItem[] => {
  if (isOrglessUser(user)) {
    return [];
  }
  if (isOperatorUser(user)) {
    return items.filter((item) => (item.key === "routes" && user.accountMode !== "lead_only") || item.key === "growth" || (item.key === "calendar" && hasCalendarAccess(user)));
  }
  if (user.accountMode === "lead_only") return items.filter((item) => item.key === "growth" || item.key === "inbox");
  return items.filter(item => item.key !== "calendar");
};
